import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { User } from './models/User.model';
import { OIDCProfile } from './types';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user = new BehaviorSubject<User | null>(null);

  private _authenticated = false;
  private readonly _authenticationChanged: BehaviorSubject<boolean> = new BehaviorSubject(this.authenticated);

  constructor(
    private readonly oauthService: OAuthService,
    private readonly authConfig: AuthConfig,
    private readonly router: Router,
  ) {}

  get authenticated(): boolean {
    return this._authenticated;
  }

  get authenticationChanged(): Observable<boolean> {
    return this._authenticationChanged;
  }

  getUser(): Observable<OIDCProfile> {
    return from(this.oauthService.loadUserProfile());
  }

  silentLogin(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.oauthService.configure(this.authConfig);
      this.oauthService.setupAutomaticSilentRefresh();

      this.oauthService.strictDiscoveryDocumentValidation = false;

      this.oauthService
        .loadDiscoveryDocumentAndLogin()
        .then(() => {
          // debugger;
          this._authenticated = this.oauthService.hasValidAccessToken();
          if (!this.oauthService.hasValidIdToken() || !this.authenticated) {
            this.oauthService.initLoginFlow();
          }

          this._authenticationChanged.next(this.authenticated);
          subscriber.next(this.authenticated);
          subscriber.complete();

          if (this.authenticated) {
            this.getUser().subscribe((data) => {
              const user = new User();
              this.mapUserData(user, data);
            });
          }
        })
        .catch((err) => {
          console.error(err);
          subscriber.next(this.authenticated);
          subscriber.complete();
        });
    });
  }

  logout(): void {
    this.oauthService.revokeTokenAndLogout();
    this._authenticated = false;
    this._authenticationChanged.next(false);

    this.router.navigate(['/', 'auth', 'login']);
  }

  private mapUserData(user: User, userData: OIDCProfile): void {
    user.id = userData.info?.sub;
    user.firstName = userData.info?.given_name ?? '';
    user.lastName = userData.info?.family_name ?? '';
    user.email = userData.info?.email ?? '';
    user.roles = Object.keys(userData.info['urn:zitadel:iam:org:project:roles'] ?? {}) ?? [];
    this.user.next(user);
  }
}
